import { clearingApi } from "../../config/apiConfig";
import type {
  CensusImportMonth,
  CensusImportResponse,
  FleetBackfillResponse,
  FleetDaysResponse,
  FleetKpiResponse,
  FleetPeriodsResponse,
  FleetResult,
  Granularity,
  KpiHistoryImportMonth,
  KpiHistoryImportResponse,
  ProjectionSeedResponse,
} from "../../types/chargingStationDashboard";

const BASE = "/api/v1/analytics/fleet";

/**
 * BOF-76 charging station dashboard.
 *
 * Deliberately the same shape as `station-performance.js`: nothing here throws, and a
 * `503 { error: "NEVER_COMPUTED" }` is surfaced as `neverComputed` rather than collapsed into a
 * generic failure. "This period has never been computed" has a remedy — run the backfill — and
 * "the fleet did nothing" does not; rendering them the same way makes a reader draw the wrong
 * conclusion (FR-054).
 *
 * The reads are bounded by date range rather than paginated, so a caller never assembles a total
 * from pages and can never present a partial one as complete (FR-056).
 */
const toFailure = (error: any, context: string): FleetResult<never> => {
  console.error(`Failed to ${context}:`, error);
  const status = error?.response?.status;
  const body = error?.response?.data;
  if (status === 503 && body?.error === "NEVER_COMPUTED") {
    return {
      success: false,
      neverComputed: true,
      months: body?.months ?? [],
      message: "This period has not been computed yet.",
      hint: body?.hint ?? null,
    };
  }
  return { success: false, neverComputed: false, message: `Could not ${context}: ${reasonOf(error, status, body)}` };
};

/**
 * Why a request failed, in words a reader can act on: the server's own `error` when it gave one, else
 * what the status means. A 404 here is never "no data" — every fleet read answers an empty range with a
 * 503 — so it means the deployed clearing does not have this endpoint.
 */
const reasonOf = (error: any, status: number | undefined, body: any): string => {
  if (typeof body?.error === "string" && body.error) return body.error;
  if (status === 404) return "clearing has no such endpoint (404). The deployed version predates this feature.";
  if (status === 401) return "your session has expired (401). Sign in again.";
  if (status === 403) return "your account lacks the admin permission (403).";
  if (typeof body === "string" && body.trim()) return `${body.trim().slice(0, 200)} (${status})`;
  if (status) return `the server answered ${status}.`;
  return error?.message ? `${error.message}.` : "the server could not be reached.";
};

/**
 * The stored daily rows in a range — the primitive every other figure is summed from.
 *
 * `from` and `to` are inclusive Berlin-local dates. The server caps the span at 800 days and answers
 * a longer one with 400 rather than truncating, so a short answer is never mistaken for a complete one.
 */
export const getFleetDays = async ({
  from,
  to,
}: {
  from: string;
  to: string;
}): Promise<FleetResult<FleetDaysResponse>> => {
  try {
    const response = await clearingApi.get(`${BASE}/days`, { params: { from, to } });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `load the fleet days for ${from} to ${to}`);
  }
};

/**
 * Whole periods over the same days, grouped by the server so the calendar rules live in one place.
 *
 * `MONTH_ADJUSTED` scales activity to a 30-day basis and leaves the charge-point census untouched —
 * scaling a count would invent charge points (FR-036).
 */
export const getFleetPeriods = async ({
  granularity,
  from,
  to,
  includeIncomplete = true,
}: {
  granularity: Granularity;
  from: string;
  to: string;
  includeIncomplete?: boolean;
}): Promise<FleetResult<FleetPeriodsResponse>> => {
  try {
    const response = await clearingApi.get(`${BASE}/periods`, {
      params: { granularity, from, to, includeIncomplete },
    });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `load the ${granularity.toLowerCase().replace("_", " ")} figures for ${from} to ${to}`);
  }
};

/** The monthly commercial scorecard. `from` and `to` are `YYYY-MM`. */
export const getFleetKpi = async ({
  from,
  to,
}: {
  from: string;
  to: string;
}): Promise<FleetResult<FleetKpiResponse>> => {
  try {
    const response = await clearingApi.get(`${BASE}/kpi`, { params: { from, to } });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `load the KPI scorecard for ${from} to ${to}`);
  }
};

/**
 * Recomputes daily rows from the sessions already stored. Idempotent — replace, never merge.
 *
 * Activity only: the census is a snapshot of how the fleet stood on a day, and a snapshot that was
 * never taken cannot be recovered. Backfilled days carry no census, and the response says so.
 */
export const backfillFleetDays = async ({
  from,
  to,
}: {
  from: string;
  to: string;
}): Promise<FleetResult<FleetBackfillResponse>> => {
  try {
    const response = await clearingApi.post(`${BASE}/backfill`, { from, to });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, `recompute the fleet days for ${from} to ${to}`);
  }
};

/**
 * Imports the workbook's hand-kept charge-point history, one figure per completed month, as each
 * month's end-of-month census. `dryRun` writes nothing and reports what an import would do, month by
 * month. Re-runnable: a re-import replaces an earlier import and never a nightly snapshot.
 */
export const importCensusHistory = async ({
  months,
  dryRun,
}: {
  months: CensusImportMonth[];
  dryRun: boolean;
}): Promise<FleetResult<CensusImportResponse>> => {
  try {
    const response = await clearingApi.post(`${BASE}/census/import`, { months, dryRun });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, dryRun ? "preview the charge-point history import" : "import the charge-point history");
  }
};

/**
 * Reads every charge point's power type and status from station-ops into clearing — the projection seed.
 *
 * Runs with the signed-in admin's own token, which clearing forwards to station-ops. Until it has run
 * once, clearing records no nightly charge-point count and no AC/DC split. Re-runnable; afterwards,
 * events keep the list current.
 */
export const syncChargePoints = async (): Promise<FleetResult<ProjectionSeedResponse>> => {
  try {
    const response = await clearingApi.post(`${BASE}/projection/seed`);
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, "sync the charge points from station-ops");
  }
};

/**
 * Imports the workbook's hand-kept KPI history (invoiced clients, service fee) for months before the
 * credit-note run history. A month a completed run reports is left to the run. `dryRun` writes nothing.
 */
export const importKpiHistory = async ({
  months,
  dryRun,
}: {
  months: KpiHistoryImportMonth[];
  dryRun: boolean;
}): Promise<FleetResult<KpiHistoryImportResponse>> => {
  try {
    const response = await clearingApi.post(`${BASE}/kpi/history/import`, { months, dryRun });
    return { success: true, data: response?.data };
  } catch (error) {
    return toFailure(error, dryRun ? "preview the KPI history import" : "import the KPI history");
  }
};
